import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LiveClassResponseDTO } from '../../shared/core/state/live-class/models/live-class';
import { Store } from '@ngrx/store';
import { State } from '../../shared/core/state';

import * as LiveClassActions from '../../shared/core/state/live-class/live-class.action';
import {
  PaymentType,
  Subscription,
  SubscriptionStatus,
} from '../../shared/core/state/subscription/subscription.model';
import { FormatterService } from '../../shared/util/formatter.service';
import { toLiveClassCardViewModel } from '../../shared/util/ui-card.mapper';
import { UiCardViewModel } from '@black/ui';
import {
  getNextSessionPerMeeting,
  SessionOccurrenceItem,
  getMeetingPlanBreakdown,
  MeetingPlanBreakdownItem,
  ExcludedSinglePassEvent,
  getEffectiveMeetingAccess,
  MeetingAccessResult,
} from '@black/shared';

@Component({
  selector: 'black-add-subscription',
  templateUrl: './add-subscription.component.html',
  styleUrl: './add-subscription.component.css',
  standalone: false,
})
export class AddSubscriptionComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private store = inject<Store<State>>(Store);
  private formatter = inject(FormatterService);

  selectedLiveClass: LiveClassResponseDTO | null;
  selectedLiveClassCard: UiCardViewModel | null;
  selectedPaymentPlanData:
    | {
        startDate: Date;
        endDate: Date;
        totalSessions: number;
        cost: number;
      }
    | undefined;

  upcomingSessions: SessionOccurrenceItem[] = [];
  selectedSessionIndex = 0;
  _selectedPaymentTypeIndex = 1;
  isBreakdownModalOpen = false;

  constructor() {
    this.selectedLiveClass = null;
    this.selectedLiveClassCard = null;
  }

  get pendingSubscription(): Subscription | null {
    if (
      !this.selectedLiveClass?.subscriptions ||
      !Array.isArray(this.selectedLiveClass.subscriptions)
    ) {
      return null;
    }
    return (
      this.selectedLiveClass.subscriptions.find(
        (sub) => sub.status === SubscriptionStatus.PENDING
      ) || null
    );
  }

  get activeMonthlySubscription(): Subscription | null {
    if (
      !this.selectedLiveClass?.subscriptions ||
      !Array.isArray(this.selectedLiveClass.subscriptions)
    ) {
      return null;
    }
    return (
      this.selectedLiveClass.subscriptions.find(
        (sub) =>
          sub.paymentType === PaymentType.MONTHLY &&
          [
            SubscriptionStatus.ACTIVE,
            SubscriptionStatus.COMPLETE,
            SubscriptionStatus.RENEWAL_DUE,
          ].includes(sub.status)
      ) || null
    );
  }

  get hasActiveMonthlySubscription(): boolean {
    return !!this.activeMonthlySubscription;
  }

  get activeFullAccessSubscription(): Subscription | null {
    if (
      !this.selectedLiveClass?.subscriptions ||
      !Array.isArray(this.selectedLiveClass.subscriptions)
    ) {
      return null;
    }
    return (
      this.selectedLiveClass.subscriptions.find(
        (sub) =>
          sub.paymentType === PaymentType.SINGLE &&
          [SubscriptionStatus.ACTIVE, SubscriptionStatus.COMPLETE].includes(
            sub.status
          )
      ) || null
    );
  }

  get hasActiveFullAccessSubscription(): boolean {
    return !!this.activeFullAccessSubscription;
  }

  get existingSinglePasses(): Subscription[] {
    if (
      !this.selectedLiveClass?.subscriptions ||
      !Array.isArray(this.selectedLiveClass.subscriptions)
    ) {
      return [];
    }
    return this.selectedLiveClass.subscriptions.filter(
      (sub) =>
        sub.paymentType === PaymentType.SINGLE_EVENT &&
        [SubscriptionStatus.ACTIVE, SubscriptionStatus.COMPLETE].includes(
          sub.status
        ) &&
        sub.targetEvent?.eventDate
    );
  }

  getExcludedSinglePassEvents(
    startDate?: Date,
    endDate?: Date
  ): ExcludedSinglePassEvent[] {
    if (!startDate || !endDate || this.existingSinglePasses.length === 0) {
      return [];
    }
    const defaultPrice = this.selectedLiveClass?.settings?.price ?? 0;
    const startMs = new Date(startDate).getTime();
    const endMs = new Date(endDate).getTime();

    return this.existingSinglePasses
      .filter((sp) => {
        const evDate = sp.targetEvent?.eventDate
          ? new Date(sp.targetEvent.eventDate)
          : null;
        if (!evDate) return false;
        return evDate.getTime() >= startMs && evDate.getTime() <= endMs;
      })
      .map((sp) => {
        const meetingId = sp.targetEvent?.meetingId?.toString();
        const targetMeeting = this.selectedLiveClass?.meetings?.find(
          (m) => (m.id || (m as any)._id)?.toString() === meetingId
        );
        const meetingPrice =
          targetMeeting?.price != null ? targetMeeting.price : defaultPrice;

        return {
          meetingId,
          eventDate: sp.targetEvent!.eventDate,
          sessionName: targetMeeting?.name || 'Class Session',
          price: meetingPrice,
        };
      });
  }

  get activeExcludedEvents(): ExcludedSinglePassEvent[] {
    if (this.selectedPaymentTypeIndex === 2 || !this.selectedPaymentPlanData) {
      return [];
    }
    return this.getExcludedSinglePassEvents(
      this.selectedPaymentPlanData.startDate,
      this.selectedPaymentPlanData.endDate
    );
  }

  get upgradeCreditAmount(): number {
    const defaultPrice = this.selectedLiveClass?.settings?.price ?? 0;
    return this.activeExcludedEvents.reduce(
      (acc, ev) => acc + (ev.price != null ? ev.price : defaultPrice),
      0
    );
  }

  get upgradeCreditSessionsCount(): number {
    return this.activeExcludedEvents.length;
  }

  updatePaymentPlanData() {
    const defaultPrice = this.selectedLiveClass?.settings?.price ?? 0;

    if (this.selectedPaymentTypeIndex === 1) {
      const basePlan = this.selectedLiveClass?.moreInfo?.paymentPlans?.oneTime;
      if (basePlan) {
        let upgradeStartDate = basePlan.startDate;
        if (this.activeMonthlySubscription?.nextPaymentDeadline) {
          const deadline = new Date(
            this.activeMonthlySubscription.nextPaymentDeadline,
          );
          if (deadline.getTime() > new Date(basePlan.startDate).getTime()) {
            upgradeStartDate = deadline;
          }
        }

        const excludedEvents = this.getExcludedSinglePassEvents(
          upgradeStartDate,
          basePlan.endDate,
        );

        const breakdown = getMeetingPlanBreakdown(
          (this.selectedLiveClass?.meetings || []) as any,
          upgradeStartDate,
          basePlan.endDate,
          defaultPrice,
          excludedEvents,
        );

        const totalRemainingSessions = breakdown.reduce(
          (acc, item) => acc + (item.isCredit ? 0 : item.sessionCount),
          0,
        );
        const totalRemainingCost = Math.max(
          0,
          breakdown.reduce((acc, item) => acc + item.subtotal, 0),
        );

        this.selectedPaymentPlanData = {
          startDate: upgradeStartDate,
          endDate: basePlan.endDate,
          totalSessions: totalRemainingSessions,
          cost: totalRemainingCost,
        };
      }
    } else if (this.selectedPaymentTypeIndex === 0) {
      const basePlan = this.selectedLiveClass?.moreInfo?.paymentPlans?.monthly;
      if (basePlan) {
        if (this.hasActiveMonthlySubscription) {
          this.selectedPaymentPlanData = {
            startDate: basePlan.startDate,
            endDate: basePlan.endDate,
            totalSessions: basePlan.totalSessions,
            cost: 0,
          };
        } else {
          const excludedEvents = this.getExcludedSinglePassEvents(
            basePlan.startDate,
            basePlan.endDate,
          );
          const totalCredit = excludedEvents.reduce(
            (acc, ev) => acc + (ev.price != null ? ev.price : defaultPrice),
            0,
          );
          this.selectedPaymentPlanData = {
            startDate: basePlan.startDate,
            endDate: basePlan.endDate,
            totalSessions: Math.max(
              0,
              basePlan.totalSessions - excludedEvents.length,
            ),
            cost: Math.max(0, basePlan.cost - totalCredit),
          };
        }
      }
    } else if (this.selectedPaymentTypeIndex === 2) {
      const session = this.upcomingSessions[this.selectedSessionIndex];
      const sessionDate = session?.date ?? new Date();
      const sessionPrice =
        session?.price != null ? session.price : defaultPrice;
      this.selectedPaymentPlanData = {
        startDate: sessionDate,
        endDate: sessionDate,
        totalSessions: 1,
        cost: sessionPrice,
      };
    }
  }

  get effectivePricePerSession(): number {
    if (this.selectedPaymentTypeIndex === 2) {
      const session = this.selectedSession;
      return session?.price != null
        ? session.price
        : (this.selectedLiveClass?.settings?.price ?? 0);
    }
    return this.selectedLiveClass?.settings?.price ?? 0;
  }

  onSessionChange(index: number | string) {
    this.selectedSessionIndex = Number(index);
    this.updatePaymentPlanData();
  }

  getSessionAccess(
    session: SessionOccurrenceItem | null,
  ): MeetingAccessResult {
    if (!session) {
      return {
        hasAccess: false,
        accessType: 'NONE',
        effectiveSubscription: null,
        isPending: false,
      };
    }
    const targetMeeting = this.selectedLiveClass?.meetings?.find(
      (m) =>
        (m.id || (m as any)._id)?.toString() === session.meetingId?.toString(),
    );
    return getEffectiveMeetingAccess({
      subscriptions: this.selectedLiveClass?.subscriptions,
      meeting: targetMeeting || { id: session.meetingId },
      targetDate: session.date,
      now: new Date(),
    });
  }

  get currentSelectedSessionAccess(): MeetingAccessResult {
    return this.getSessionAccess(this.selectedSession);
  }

  get isCurrentSelectedSessionCovered(): boolean {
    if (this.selectedPaymentTypeIndex !== 2) return false;
    return this.currentSelectedSessionAccess.hasAccess;
  }

  ngOnInit(): void {
    const liveClassId = this.route.snapshot.paramMap.get('id');
    const planParam = this.route.snapshot.queryParamMap.get('plan');
    const meetingIdParam = this.route.snapshot.queryParamMap.get('meetingId');

    if (planParam === 'single') {
      this._selectedPaymentTypeIndex = 2;
    }

    if (liveClassId) {
      this.store.dispatch(
        LiveClassActions.selectLiveClass({ id: liveClassId })
      );
    }

    this.store
      .select((state) => state.liveClass.selectedLiveClassItem)
      .subscribe((item) => {
        if (item) {
          this.selectedLiveClass = item;
          this.selectedLiveClassCard = toLiveClassCardViewModel(
            item,
            this.formatter,
            {
              showButtons: false,
            }
          );
          if (item.meetings && Array.isArray(item.meetings)) {
            this.upcomingSessions = getNextSessionPerMeeting(
              item.meetings as any,
            );
            if (meetingIdParam) {
              const targetIdx = this.upcomingSessions.findIndex(
                (s) => s.meetingId?.toString() === meetingIdParam.toString(),
              );
              if (targetIdx >= 0) {
                this.selectedSessionIndex = targetIdx;
              }
            }
          }
          this.updatePaymentPlanData();
        }
      });
  }

  get selectedPaymentTypeIndex() {
    return this._selectedPaymentTypeIndex;
  }
  set selectedPaymentTypeIndex(value) {
    this._selectedPaymentTypeIndex = value;
    this.updatePaymentPlanData();
  }

  createSubscription() {
    let paymentType: PaymentType = PaymentType.MONTHLY;
    let targetEvent: { meetingId: string; eventDate: Date } | undefined;

    if (this.selectedPaymentTypeIndex === 1) {
      paymentType = PaymentType.SINGLE;
    } else if (this.selectedPaymentTypeIndex === 2) {
      paymentType = PaymentType.SINGLE_EVENT;
      const selectedSession = this.upcomingSessions[this.selectedSessionIndex];
      if (selectedSession) {
        targetEvent = {
          meetingId: selectedSession.meetingId || '',
          eventDate: selectedSession.date,
        };
      }
    }

    if (this.selectedLiveClass?.id) {
      this.store.dispatch(
        LiveClassActions.subscribeToLiveClass({
          id: this.selectedLiveClass.id,
          paymentType,
          targetEvent,
        })
      );
    }
  }

  get selectedSession(): SessionOccurrenceItem | null {
    return this.upcomingSessions[this.selectedSessionIndex] || null;
  }

  get planTitle(): string {
    if (this.selectedPaymentTypeIndex === 0) return 'Month Flexi';
    if (this.selectedPaymentTypeIndex === 1) return 'Full Course Access';
    if (this.selectedPaymentTypeIndex === 2) return 'Single Class Pass';
    return 'Subscription';
  }

  get planBadge(): string {
    if (this.selectedPaymentTypeIndex === 0) return 'Monthly Recurring';
    if (this.selectedPaymentTypeIndex === 1) return 'One-Time Payment';
    if (this.selectedPaymentTypeIndex === 2) return 'One-Off Pass';
    return '';
  }

  get planNote(): string {
    if (this.selectedPaymentTypeIndex === 0) {
      return 'Covers all scheduled sessions in the upcoming 1-month billing cycle. Renews automatically each month; cancel anytime.';
    }
    if (this.selectedPaymentTypeIndex === 1) {
      return 'Covers all remaining sessions until the course ends. One single payment with no recurring charges.';
    }
    if (this.selectedPaymentTypeIndex === 2) {
      return 'Grants live access to the selected class session only. One-off payment with no recurring renewals.';
    }
    return '';
  }

  get planBreakdownItems(): MeetingPlanBreakdownItem[] {
    if (this.selectedPaymentTypeIndex === 0 && this.hasActiveMonthlySubscription) {
      const deadlineStr = this.activeMonthlySubscription?.nextPaymentDeadline
        ? new Date(
            this.activeMonthlySubscription.nextPaymentDeadline,
          ).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : 'Active';
      return [
        {
          name: 'Current Monthly Plan (Active)',
          recurrenceLabel: `Covered until ${deadlineStr}`,
          timeRange: 'Active Subscription',
          sessionCount: this.selectedPaymentPlanData?.totalSessions ?? 0,
          pricePerSession: 0,
          subtotal: 0,
        },
      ];
    }

    if (this.selectedPaymentTypeIndex === 2) {
      const session = this.selectedSession;
      if (!session) return [];
      return [
        {
          meetingId: session.meetingId,
          name: session.name,
          recurrenceLabel: 'Single Selected Session',
          timeRange: session.startTime
            ? `${session.startTime} – ${session.endTime || 'End'}`
            : '',
          sessionCount: 1,
          pricePerSession: this.effectivePricePerSession,
          subtotal: this.effectivePricePerSession,
        },
      ];
    }

    return getMeetingPlanBreakdown(
      (this.selectedLiveClass?.meetings || []) as any,
      this.selectedPaymentPlanData?.startDate,
      this.selectedPaymentPlanData?.endDate,
      this.selectedLiveClass?.settings?.price ?? 0,
      this.activeExcludedEvents,
    );
  }

  get breakdownTotalSessions(): number {
    return this.planBreakdownItems.reduce(
      (acc, item) => acc + (item.isCredit ? -1 : item.sessionCount),
      0
    );
  }

  get breakdownTotalCost(): number {
    return Math.max(
      0,
      this.planBreakdownItems.reduce((acc, item) => acc + item.subtotal, 0)
    );
  }

  openBreakdownModal() {
    this.isBreakdownModalOpen = true;
  }

  closeBreakdownModal() {
    this.isBreakdownModalOpen = false;
  }

  get paymentOptions() {
    const currency = 'LKR';
    const monthlyCost = this.selectedLiveClass?.moreInfo?.paymentPlans?.monthly?.cost;
    const oneTimeCost = this.selectedLiveClass?.moreInfo?.paymentPlans?.oneTime?.cost;

    if (this.hasActiveFullAccessSubscription) {
      return [
        {
          title: 'MONTH FLEXI',
          subtitle: 'Full course access active (all sessions unlocked).',
          badge: 'Included',
        },
        {
          title: 'ONE TIME (FULL ACCESS)',
          subtitle: 'You own lifetime access to this entire course.',
          badge: 'Active',
        },
        {
          title: 'SINGLE CLASS',
          subtitle:
            'All sessions are already included in your full access plan.',
          badge: 'Included',
        },
      ];
    }

    let monthlyTitle = 'MONTH FLEXI';
    let monthlySubtitle =
      'Pay monthly based on the number of sessions. Flexibility to cancel anytime.';
    let monthlyBadge: string | undefined;
    if (this.hasActiveMonthlySubscription) {
      monthlyTitle = 'MONTH FLEXI (CURRENT PLAN)';
      const deadlineStr = this.activeMonthlySubscription?.nextPaymentDeadline
        ? new Date(
            this.activeMonthlySubscription.nextPaymentDeadline,
          ).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : 'Active';
      monthlySubtitle = `You are currently enrolled on Monthly Flexi (active until ${deadlineStr}).`;
      monthlyBadge = 'Current Plan';
    } else if (this.existingSinglePasses.length > 0) {
      monthlyTitle = 'UPGRADE TO MONTHLY';
      monthlySubtitle =
        'Upgrade to Monthly Flexi with your pre-paid session credits automatically applied.';
      monthlyBadge = 'Upgrade';
    }

    let oneTimeTitle = 'ONE TIME';
    let oneTimeSubtitle =
      'Pay once and access the full course without further payments.';
    let oneTimeBadge: string | undefined;
    if (this.hasActiveMonthlySubscription) {
      oneTimeTitle = 'UPGRADE TO FULL ACCESS';
      oneTimeSubtitle =
        'Upgrade to lifetime access for all remaining course sessions without recurring monthly billing.';
      oneTimeBadge = 'Upgrade';
    } else if (this.existingSinglePasses.length > 0) {
      oneTimeTitle = 'UPGRADE TO FULL ACCESS';
      oneTimeSubtitle =
        'Upgrade to full lifetime access with your pre-paid session credits applied.';
      oneTimeBadge = 'Upgrade';
    } else {
      oneTimeBadge = 'Best Value';
    }

    return [
      {
        title: monthlyTitle,
        subtitle: monthlySubtitle,
        price: monthlyCost != null ? `${currency} ${monthlyCost.toLocaleString()}` : undefined,
        badge: monthlyBadge,
      },
      {
        title: oneTimeTitle,
        subtitle: oneTimeSubtitle,
        price: oneTimeCost != null ? `${currency} ${oneTimeCost.toLocaleString()}` : undefined,
        badge: oneTimeBadge,
      },
      {
        title: 'SINGLE CLASS',
        subtitle: 'Pay for a single upcoming class session of your choice.',
        price: this.effectivePricePerSession ? `${currency} ${this.effectivePricePerSession.toLocaleString()}` : undefined,
      },
    ];
  }
}
